import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Separator } from '@/components/ui/separator'
import { Badge } from '@/components/ui/badge'
import { Switch } from '@/components/ui/switch'
import { Combobox, ComboboxOption } from '@/components/ui/combobox'
import {
  ArrowLeft,
  Save,
  User,
  Mail,
  Phone,
  CreditCard,
  Building,
  Hash,
  Loader2,
  CheckCircle2,
  AlertCircle,
  Plus,
  Trash2
} from 'lucide-react'
import { useUnsavedChanges } from '@/hooks/useUnsavedChanges'
import { useAuth } from '@/contexts/AuthContext'
import { useDriverMaster } from '@/stores/driverMasterStore'
import { DriverMaster, UpdateDriverMasterData, CreateAliasData, CreateDriverMasterData } from '@/types/driverMaster'
import { formatDate } from '@/lib/formatters'
import {
  PageContainer,
  PageHeader,
  DataCard,
  LoadingState
} from '@/components/common'
import { CreateAliasDialog } from '@/components/driver-master/CreateAliasDialog'
import { ConfirmDialog } from '@/components/common/dialogs/ConfirmDialog'
import { BaseDialog } from '@/components/common/dialogs/BaseDialog'
import { Checkbox } from '@/components/ui/checkbox'
import { PlatformBadge } from '@/components/ui/PlatformBadge'
import { TaxCalculationConfig } from '@/components/config/TaxCalculationConfig'
import { useConfigStore } from '@/stores/configStore'
import { toast } from 'sonner'
import { useFeatureFlag } from '@/hooks/useFeatureFlag'
import { FeatureFlag } from '@/constants/featureFlags'
import { payoutCompanyService } from '@/services/payoutCompanyService'
import { PayoutCompany } from '@/types'

export function DriverDetailPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { currentUser } = useAuth()
  const multiCompanyEnabled = useFeatureFlag(FeatureFlag.MULTI_COMPANY)

  const {
    filteredDrivers,
    loading,
    error,
    updateDriver,
    createDriver,
    createAlias,
    deleteAlias,
    deleteDriver,
    getDriverDataInOpenPeriods,
    clearError,
    loadDrivers
  } = useDriverMaster()

  // Determine if this is create mode
  const isCreateMode = id === 'new'

  // Local state
  const [saving, setSaving] = useState(false)
  const [success, setSuccess] = useState(false)
  const [localError, setLocalError] = useState<string | null>(null)
  const [showCreateAlias, setShowCreateAlias] = useState(false)
  const [deletingAlias, setDeletingAlias] = useState<{ driverId: string, aliasIndex: number } | null>(null)
  const [deletingDriver, setDeletingDriver] = useState(false)
  const [deletingDriverLoading, setDeletingDriverLoading] = useState(false)
  const [checkingDriverData, setCheckingDriverData] = useState(false)
  const [driverDataCounts, setDriverDataCounts] = useState<{ earningsCount: number; compensationsCount: number } | null>(null)
  const [deleteDataConfirmed, setDeleteDataConfirmed] = useState(false)
  const [newlyCreatedDriver, setNewlyCreatedDriver] = useState<DriverMaster | null>(null)

  // Payout company state
  const [payoutCompanies, setPayoutCompanies] = useState<PayoutCompany[]>([])

  // Form data
  const [formData, setFormData] = useState<UpdateDriverMasterData>({})
  const [originalFormData, setOriginalFormData] = useState<UpdateDriverMasterData>({})

  // Tax configuration state
  const [availableTables, setAvailableTables] = useState<number[]>([31])
  const [useIndividualTaxSettings, setUseIndividualTaxSettings] = useState(false)
  const { taxTableLoading, taxTableLoaded, getAvailableTables } = useConfigStore()

  const hasChanges = JSON.stringify(formData) !== JSON.stringify(originalFormData)
  useUnsavedChanges(hasChanges)

  // Load drivers on component mount if needed (skip for create mode)
  useEffect(() => {
    if (!isCreateMode && currentUser && filteredDrivers.length === 0 && !loading) {
      loadDrivers(currentUser)
    }
  }, [isCreateMode, currentUser, filteredDrivers.length, loading, loadDrivers])

  // Load available tax tables
  useEffect(() => {
    const loadTables = async () => {
      try {
        const tables = await getAvailableTables()
        setAvailableTables(tables)
      } catch (error) {
        console.error('Failed to load tax tables:', error)
      }
    }
    loadTables()
  }, [getAvailableTables])

  // Load payout companies when feature flag is enabled
  useEffect(() => {
    if (!multiCompanyEnabled || !currentUser) return
    payoutCompanyService.getPayoutCompanies(currentUser.uid)
      .then(setPayoutCompanies)
      .catch(err => console.error('Failed to load payout companies:', err))
  }, [multiCompanyEnabled, currentUser])

  // Find the driver (null for create mode)
  // Prioritize newly created driver if it matches the current id, otherwise look in filteredDrivers
  const driver = isCreateMode
    ? null
    : (newlyCreatedDriver?.id === id ? newlyCreatedDriver : filteredDrivers.find(d => d.id === id))

  // Reset states when id changes
  useEffect(() => {
    setSuccess(false)
    setLocalError(null)
  }, [id, isCreateMode])

  // Initialize form data when driver changes or in create mode
  useEffect(() => {
    if (isCreateMode) {
      // Initialize with empty data for create mode
      const initial: UpdateDriverMasterData = {
        primaryName: '',
        email: '',
        phone: '',
        personalCode: '',
        taxiCode: '',
        employeeNumber: '',
        bankAccount: '',
        paymentReference: '',
        taxCalculationMethod: undefined,
        taxRate: undefined,
        taxTableName: undefined,
        taxColumn: undefined,
        payoutCompanyId: undefined
      }
      setFormData(initial)
      setOriginalFormData(initial)
      setUseIndividualTaxSettings(false)
    } else if (driver) {
      // Initialize with driver data for edit mode
      const loaded: UpdateDriverMasterData = {
        primaryName: driver.primaryName,
        email: driver.email || '',
        phone: driver.phone || '',
        personalCode: driver.personalCode || '',
        taxiCode: driver.taxiCode || '',
        employeeNumber: driver.employeeNumber || '',
        bankAccount: driver.bankAccount || '',
        paymentReference: driver.paymentReference || '',
        taxCalculationMethod: driver.taxCalculationMethod,
        taxRate: driver.taxRate,
        taxTableName: driver.taxTableName || undefined,
        taxColumn: driver.taxColumn || undefined,
        payoutCompanyId: driver.payoutCompanyId || undefined
      }
      setFormData(loaded)
      setOriginalFormData(loaded)
      // Set toggle based on whether driver has individual tax settings
      // Check for both undefined and null (Firestore may store as null)
      setUseIndividualTaxSettings(driver.taxCalculationMethod !== undefined && driver.taxCalculationMethod !== null)
    }
  }, [isCreateMode, driver])

  // Clear success message after delay
  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => setSuccess(false), 3000)
      return () => clearTimeout(timer)
    }
  }, [success])

  // Type-safe handlers for different input types
  const handleTextChange = (
    field: 'primaryName' | 'email' | 'phone' | 'personalCode' | 'taxiCode' | 'employeeNumber' | 'bankAccount' | 'paymentReference',
    value: string
  ) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }))

    // Clear errors when user starts typing
    if (localError || error) {
      setLocalError(null)
      clearError()
    }
  }

  const handleTaxMethodChange = (value: 'fixed' | 'table') => {
    setFormData(prev => ({
      ...prev,
      taxCalculationMethod: value
    }))

    // Clear errors when user starts typing
    if (localError || error) {
      setLocalError(null)
      clearError()
    }
  }

  const handleTaxRateChange = (value: number | undefined) => {
    setFormData(prev => ({
      ...prev,
      taxRate: value
    }))

    // Clear errors when user starts typing
    if (localError || error) {
      setLocalError(null)
      clearError()
    }
  }

  const handleTaxTableNameChange = (value: string | undefined) => {
    setFormData(prev => ({
      ...prev,
      taxTableName: value
    }))

    // Clear errors when user starts typing
    if (localError || error) {
      setLocalError(null)
      clearError()
    }
  }

  const handleTaxColumnChange = (value: number | undefined) => {
    setFormData(prev => ({
      ...prev,
      taxColumn: value
    }))

    // Clear errors when user starts typing
    if (localError || error) {
      setLocalError(null)
      clearError()
    }
  }

  // Generic wrapper for TaxCalculationConfig that routes to type-safe handlers
  const handleTaxConfigChange = (field: string, value: any) => {
    switch (field) {
      case 'taxCalculationMethod':
        if (value === 'fixed' || value === 'table') {
          handleTaxMethodChange(value)
        }
        break
      case 'taxRate':
        handleTaxRateChange(value)
        break
      case 'taxTableName':
        handleTaxTableNameChange(value)
        break
      case 'taxColumn':
        handleTaxColumnChange(value)
        break
    }
  }

  // Handle toggle change for individual tax settings
  const handleToggleTaxSettings = (checked: boolean) => {
    setUseIndividualTaxSettings(checked)

    if (checked) {
      // Toggle ON: Set default to 'fixed' method if no method is set
      if (!formData.taxCalculationMethod) {
        setFormData(prev => ({
          ...prev,
          taxCalculationMethod: 'fixed'
        }))
      }
    } else {
      // Toggle OFF: Clear all tax settings (use global)
      setFormData(prev => ({
        ...prev,
        taxCalculationMethod: undefined,
        taxRate: undefined,
        taxTableName: undefined,
        taxColumn: undefined
      }))
    }
  }

  // Handle save
  const handleSave = async () => {
    if (!currentUser) return

    // Basic validation
    if (!formData.primaryName?.trim()) {
      setLocalError('Förarens namn är obligatoriskt')
      return
    }

    // Email validation
    if (formData.email && formData.email.trim()) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
      if (!emailRegex.test(formData.email.trim())) {
        setLocalError('Ogiltigt e-postformat')
        return
      }
    }

    // Phone validation
    if (formData.phone && formData.phone.trim()) {
      const phoneRegex = /^[\+]?[0-9\s\-\(\)]{8,15}$/
      if (!phoneRegex.test(formData.phone.trim())) {
        setLocalError('Ogiltigt telefonnummer')
        return
      }
    }

    // Tax settings validation (only if individual settings are enabled)
    if (useIndividualTaxSettings) {
      if (formData.taxCalculationMethod === 'fixed') {
        if (formData.taxRate === undefined || formData.taxRate === null) {
          setLocalError('Skattesats är obligatorisk när fast procentsats är vald')
          return
        }
        if (formData.taxRate < 0 || formData.taxRate > 100) {
          setLocalError('Skattesats måste vara mellan 0 och 100%')
          return
        }
      } else if (formData.taxCalculationMethod === 'table') {
        if (!formData.taxTableName || !formData.taxColumn) {
          setLocalError('Skattetabell och kolumn är obligatoriska när skattetabell är vald')
          return
        }
      }
    }

    setSaving(true)
    setLocalError(null)

    try {
      if (isCreateMode) {
        // Create new driver
        const createData: CreateDriverMasterData = {
          primaryName: formData.primaryName.trim()
        }

        // Only add fields if they have values
        if (formData.email?.trim()) {
          createData.email = formData.email.trim()
        }
        if (formData.phone?.trim()) {
          createData.phone = formData.phone.trim()
        }
        if (formData.personalCode?.trim()) {
          createData.personalCode = formData.personalCode.trim()
        }
        if (formData.taxiCode?.trim()) {
          createData.taxiCode = formData.taxiCode.trim()
        }
        if (formData.employeeNumber?.trim()) {
          createData.employeeNumber = formData.employeeNumber.trim()
        }
        if (formData.bankAccount?.trim()) {
          createData.bankAccount = formData.bankAccount.trim()
        }
        if (formData.paymentReference?.trim()) {
          createData.paymentReference = formData.paymentReference.trim()
        }
        if (formData.taxCalculationMethod) {
          createData.taxCalculationMethod = formData.taxCalculationMethod
        }
        if (formData.taxRate !== undefined) {
          createData.taxRate = formData.taxRate
        }
        if (formData.taxTableName) {
          createData.taxTableName = formData.taxTableName
        }
        if (formData.taxColumn) {
          createData.taxColumn = formData.taxColumn
        }

        const newDriver = await createDriver(currentUser, createData)

        // Save the newly created driver in local state so we can display it immediately
        setNewlyCreatedDriver(newDriver)

        // Navigate to the new driver's detail page immediately
        navigate(`/driver/${newDriver.id}`)
      } else {
        // Update existing driver
        if (!driver) return

        // Prepare update data (only include changed fields)
        const updates: UpdateDriverMasterData = {}

        if (formData.primaryName !== driver.primaryName) {
          updates.primaryName = formData.primaryName?.trim()
        }

        if (formData.email !== (driver.email || '')) {
          updates.email = formData.email?.trim() || undefined
        }

        if (formData.phone !== (driver.phone || '')) {
          updates.phone = formData.phone?.trim() || undefined
        }

        if (formData.personalCode !== (driver.personalCode || '')) {
          updates.personalCode = formData.personalCode?.trim() || undefined
        }

        if (formData.taxiCode !== (driver.taxiCode || '')) {
          updates.taxiCode = formData.taxiCode?.trim() || undefined
        }

        if (formData.employeeNumber !== (driver.employeeNumber || '')) {
          updates.employeeNumber = formData.employeeNumber?.trim() || undefined
        }

        if (formData.bankAccount !== (driver.bankAccount || '')) {
          updates.bankAccount = formData.bankAccount?.trim() || undefined
        }

        if (formData.paymentReference !== (driver.paymentReference || '')) {
          updates.paymentReference = formData.paymentReference?.trim() || undefined
        }

        if (formData.taxCalculationMethod !== driver.taxCalculationMethod) {
          updates.taxCalculationMethod = formData.taxCalculationMethod
        }

        if (formData.taxRate !== driver.taxRate) {
          updates.taxRate = formData.taxRate
        }

        if (formData.taxTableName !== (driver.taxTableName || undefined)) {
          updates.taxTableName = formData.taxTableName || undefined
        }

        if (formData.taxColumn !== (driver.taxColumn || undefined)) {
          updates.taxColumn = formData.taxColumn || undefined
        }

        if (formData.payoutCompanyId !== (driver.payoutCompanyId || undefined)) {
          updates.payoutCompanyId = formData.payoutCompanyId || null
        }

        // Only submit if there are changes
        if (Object.keys(updates).length === 0) {
          return
        }

        await updateDriver(currentUser, driver.id, updates)
        setSuccess(true)
      }
    } catch (error) {
      console.error(`Error ${isCreateMode ? 'creating' : 'updating'} driver:`, error)
      setLocalError(`Kunde inte ${isCreateMode ? 'skapa föraren' : 'spara ändringarna'}`)
    } finally {
      setSaving(false)
    }
  }



  // Handle create alias
  const handleCreateAlias = async (alias: CreateAliasData) => {
    if (!currentUser || !driver) return

    try {
      await createAlias(currentUser, driver.id, alias)
      setShowCreateAlias(false)
    } catch (error) {
      console.error('Error creating alias:', error)
    }
  }

  // Handle delete alias
  const handleDeleteAlias = async () => {
    if (!currentUser || !deletingAlias) return

    try {
      await deleteAlias(currentUser, deletingAlias.driverId, deletingAlias.aliasIndex)
      setDeletingAlias(null)
    } catch (error) {
      console.error('Error deleting alias:', error)
    }
  }

  // Computed: check if driver has data in open periods
  const hasDataInOpenPeriods = driverDataCounts && (driverDataCounts.earningsCount > 0 || driverDataCounts.compensationsCount > 0)

  // Handle initiating delete driver (check for data first)
  const handleInitiateDeleteDriver = async () => {
    if (!currentUser || !driver) return

    setCheckingDriverData(true)
    setDeleteDataConfirmed(false)
    try {
      const counts = await getDriverDataInOpenPeriods(currentUser, driver.id)
      setDriverDataCounts(counts)
    } catch (error) {
      console.error('Error checking driver data:', error)
      toast.error('Kunde inte kontrollera förarens data. Du kan fortfarande ta bort föraren.')
      // Set to null so the dialog shows without the data warning
      setDriverDataCounts(null)
    } finally {
      setCheckingDriverData(false)
      setDeletingDriver(true)
    }
  }

  // Handle delete driver
  const handleDeleteDriver = async () => {
    if (!currentUser || !driver) return

    // If driver has data in open periods but user hasn't confirmed deletion, don't proceed
    if (hasDataInOpenPeriods && !deleteDataConfirmed) {
      return
    }

    setDeletingDriverLoading(true)
    try {
      await deleteDriver(currentUser, driver.id, {
        deleteDataInOpenPeriods: deleteDataConfirmed
      })
      navigate('/forare') // Redirect to drivers list
    } catch (error) {
      console.error('Error deleting driver:', error)
    } finally {
      setDeletingDriverLoading(false)
      setDeletingDriver(false)
      setDriverDataCounts(null)
      setDeleteDataConfirmed(false)
    }
  }

  // Handle closing delete dialog
  const handleCloseDeleteDialog = (open: boolean) => {
    setDeletingDriver(open)
    if (!open) {
      setDriverDataCounts(null)
      setDeleteDataConfirmed(false)
    }
  }

  if (loading) {
    return (
      <PageContainer>
        <LoadingState
          message="Laddar förare..."
          icon={<Loader2 className="h-8 w-8 animate-spin mx-auto mb-4 text-primary" />}
        />
      </PageContainer>
    )
  }

  // Show loading state while drivers are being loaded
  if (loading && filteredDrivers.length === 0) {
    return (
      <PageContainer>
        <LoadingState message="Laddar förardata..." />
      </PageContainer>
    )
  }

  // Show error if driver not found after loading is complete (skip for create mode)
  if (!isCreateMode && !loading && !driver) {
    return (
      <PageContainer>
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <h2 className="text-xl font-semibold text-muted-foreground">Föraren hittades inte</h2>
            <p className="text-sm text-muted-foreground mt-2">
              Kontrollera att förare-ID:t är korrekt
            </p>
            <Button
              variant="outline"
              onClick={() => navigate('/forare')}
              className="mt-4"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Tillbaka till förare
            </Button>
          </div>
        </div>
      </PageContainer>
    )
  }

  // Don't render if driver is still loading (skip for create mode)
  if (!isCreateMode && !driver) {
    return null
  }

  return (
    <PageContainer>
      <PageHeader
        title={
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/forare')}
              className="p-2"
            >
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <span>{isCreateMode ? 'Ny förare' : driver?.primaryName}</span>
            {!isCreateMode && driver && (
              <Badge variant={driver.isActive ? 'default' : 'secondary'}>
                {driver.isActive ? 'Aktiv' : 'Inaktiv'}
              </Badge>
            )}
          </div>
        }
        subtitle={isCreateMode ? 'Skapa en ny förare' : 'Förardetaljer och redigeringsmöjligheter'}
      />

      {/* Error and Success Alerts */}
      {(error || localError) && (
        <Alert variant="destructive" className="mb-6">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error || localError}</AlertDescription>
        </Alert>
      )}

      {success && (
        <Alert className="border-green-200 bg-green-50 mb-6">
          <CheckCircle2 className="h-4 w-4 text-green-600" />
          <AlertDescription className="text-green-800">
            Föraren har uppdaterats framgångsrikt!
          </AlertDescription>
        </Alert>
      )}

      <div className="grid gap-6 lg:grid-cols-2">
        {/* Basic Information */}
        <DataCard title="Grundläggande information">
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="primaryName">
                Förarens namn <span className="text-red-500">*</span>
              </Label>
              <Input
                id="primaryName"
                type="text"
                placeholder="Ange förarens fullständiga namn"
                value={formData.primaryName || ''}
                onChange={(e) => handleTextChange('primaryName', e.target.value)}
                disabled={saving}
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="email">E-postadress</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="foran@example.com"
                  value={formData.email || ''}
                  onChange={(e) => handleTextChange('email', e.target.value)}
                  disabled={saving}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="phone">Telefonnummer</Label>
                <Input
                  id="phone"
                  type="tel"
                  placeholder="+46 70 123 45 67"
                  value={formData.phone || ''}
                  onChange={(e) => handleTextChange('phone', e.target.value)}
                  disabled={saving}
                />
              </div>
            </div>
          </div>
        </DataCard>

        {/* Personal Information */}
        <DataCard title="Personlig information">
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="personalCode">Personnummer</Label>
                <Input
                  id="personalCode"
                  type="text"
                  placeholder="YYYYMMDD-XXXX"
                  value={formData.personalCode || ''}
                  onChange={(e) => handleTextChange('personalCode', e.target.value)}
                  disabled={saving}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="taxiCode">Taxikod</Label>
                <Input
                  id="taxiCode"
                  type="text"
                  placeholder="T123456"
                  value={formData.taxiCode || ''}
                  onChange={(e) => handleTextChange('taxiCode', e.target.value)}
                  disabled={saving}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="employeeNumber">Anställningsnummer</Label>
              <Input
                id="employeeNumber"
                type="text"
                placeholder="Anställningsnummer för lönespecifikation"
                value={formData.employeeNumber || ''}
                onChange={(e) => handleTextChange('employeeNumber', e.target.value)}
                disabled={saving}
              />
            </div>
          </div>
        </DataCard>

        {/* Payment Information */}
        <DataCard title="Betalningsinformation">
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="bankAccount">Bankkonto</Label>
              <Input
                id="bankAccount"
                type="text"
                placeholder="1234-56 789 01"
                value={formData.bankAccount || ''}
                onChange={(e) => handleTextChange('bankAccount', e.target.value)}
                disabled={saving}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="paymentReference">Referensnummer</Label>
              <Input
                id="paymentReference"
                type="text"
                placeholder="Betalningsreferens"
                value={formData.paymentReference || ''}
                onChange={(e) => handleTextChange('paymentReference', e.target.value)}
                disabled={saving}
              />
            </div>
          </div>
        </DataCard>

        {/* Payout Company - Only shown when MULTI_COMPANY flag is on */}
        {multiCompanyEnabled && (
          <DataCard title="Utbetalande bolag">
            <div className="space-y-2">
              <Label>Bolag</Label>
              <Combobox
                options={[
                  { value: '__default__', label: 'Standard (profilbolag)' },
                  ...payoutCompanies.map(c => ({ value: c.id, label: c.name }))
                ]}
                value={formData.payoutCompanyId || '__default__'}
                onValueChange={(value) =>
                  setFormData(prev => ({
                    ...prev,
                    payoutCompanyId: value === '__default__' ? undefined : value
                  }))
                }
                disabled={saving}
                placeholder="Välj bolag..."
                searchPlaceholder="Sök bolag..."
              />
              <p className="text-xs text-muted-foreground">
                Väljer vilket bolag som visas på förarens lönespecifikation.
              </p>
            </div>
          </DataCard>
        )}

        {/* System Information - Only show in edit mode */}
        {!isCreateMode && driver && (
          <DataCard title="Systeminformation">
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-muted-foreground">Skapad:</span>
                  <div className="font-medium">{formatDate(driver.createdAt)}</div>
                </div>
                <div>
                  <span className="text-muted-foreground">Uppdaterad:</span>
                  <div className="font-medium">{formatDate(driver.updatedAt)}</div>
                </div>
                <div>
                  <span className="text-muted-foreground">ID:</span>
                  <div className="font-mono text-xs">{driver.id}</div>
                </div>
                <div>
                  <span className="text-muted-foreground">Status:</span>
                  <div>
                    <Badge variant={driver.isActive ? 'default' : 'secondary'}>
                      {driver.isActive ? 'Aktiv' : 'Inaktiv'}
                    </Badge>
                  </div>
                </div>
              </div>
            </div>
          </DataCard>
        )}
      </div>

      {/* Aliases Section - Only show in edit mode */}
      {!isCreateMode && driver && (
        <DataCard
          title={`Alias (${driver.aliases.length})`}
          action={
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowCreateAlias(true)}
            >
              <Plus className="h-4 w-4 mr-2" />
              Lägg till alias
            </Button>
          }
        >
          {driver.aliases.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <User className="h-8 w-8 mx-auto mb-2 opacity-50" />
              <p>Inga alias registrerade</p>
              <p className="text-sm">Alias hjälper till att länka ihop förare från olika plattformar</p>
            </div>
          ) : (
            <div className="space-y-3">
              {driver.aliases.map((alias, index) => (
                <div key={index} className="flex items-center justify-between p-3 border rounded-lg">
                  <div className="flex items-center gap-3">
                    <PlatformBadge platform={alias.platform} />
                    <div>
                      <div className="font-medium">{alias.name || 'Ingen namn'}</div>
                      <div className="text-sm text-muted-foreground">
                        {alias.email && <span>📧 {alias.email}</span>}
                        {alias.phone && <span className="ml-2">📱 {alias.phone}</span>}
                        {alias.uuid && <span className="ml-2">🆔 {alias.uuid}</span>}
                      </div>
                    </div>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setDeletingAlias({ driverId: driver.id, aliasIndex: index })}
                    className="text-red-600 hover:text-red-700"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              ))}
            </div>
          )}
        </DataCard>
      )}

      {/* Tax Configuration - Using shared component */}
      {!isCreateMode && (
        <DataCard title="Skatteberäkning" className="border-blue-200">
          <div className="space-y-4">
            {/* Toggle for individual tax settings */}
            <div className="flex items-center justify-between p-3 bg-muted/20 rounded-lg border border-border/50">
              <div className="flex-1">
                <Label htmlFor="useIndividualTaxSettings" className="text-sm font-medium cursor-pointer">
                  Använd individuella skatteinställningar
                </Label>
                <p className="text-xs text-muted-foreground mt-1">
                  {useIndividualTaxSettings
                    ? 'Denna förare har anpassade skatteinställningar'
                    : 'Denna förare använder globala skatteinställningar'}
                </p>
              </div>
              <Switch
                id="useIndividualTaxSettings"
                checked={useIndividualTaxSettings}
                onCheckedChange={handleToggleTaxSettings}
                disabled={saving}
              />
            </div>

            {/* Show tax config only when toggle is ON */}
            {useIndividualTaxSettings ? (
              <TaxCalculationConfig
                value={{
                  taxCalculationMethod: formData.taxCalculationMethod,
                  taxRate: formData.taxRate,
                  taxTableName: formData.taxTableName,
                  taxColumn: formData.taxColumn
                }}
                onChange={handleTaxConfigChange}
                availableTables={availableTables}
                taxTableLoading={taxTableLoading}
                taxTableLoaded={taxTableLoaded}
                disabled={saving}
                showPlaceholder={false}
              />
            ) : (
              <div className="text-sm text-muted-foreground p-3 bg-muted/10 rounded-lg border border-dashed">
                Föraren använder de globala skatteinställningarna som konfigurerats på inställningssidan.
              </div>
            )}
          </div>
        </DataCard>
      )}

      {/* Save Button */}
      <div className="flex justify-end">
        <Button
          onClick={handleSave}
          disabled={!hasChanges || saving || !formData.primaryName?.trim()}
        >
          {saving ? (
            <Loader2 className="h-4 w-4 animate-spin mr-2" />
          ) : (
            <Save className="h-4 w-4 mr-2" />
          )}
          {saving
            ? (isCreateMode ? 'Skapar...' : 'Sparar...')
            : (isCreateMode ? 'Skapa förare' : 'Spara')}
        </Button>
      </div>

      {/* Danger Zone - Only show in edit mode */}
      {!isCreateMode && (
        <DataCard title="Farlig zon" className="border-red-200">
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Dessa åtgärder är permanenta och kan inte ångras.
            </p>
            <Button
              variant="destructive"
              onClick={handleInitiateDeleteDriver}
              disabled={checkingDriverData}
            >
              {checkingDriverData ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Trash2 className="h-4 w-4 mr-2" />
              )}
              Ta bort förare
            </Button>
          </div>
        </DataCard>
      )}

      {/* Dialogs */}
      <CreateAliasDialog
        open={showCreateAlias}
        onClose={() => setShowCreateAlias(false)}
        onSubmit={handleCreateAlias}
        loading={false}
      />

      <ConfirmDialog
        open={!!deletingAlias}
        onOpenChange={(open) => !open && setDeletingAlias(null)}
        title="Ta bort alias"
        description="Är du säker på att du vill ta bort detta alias? Denna åtgärd kan inte ångras."
        confirmText="Ta bort"
        cancelText="Avbryt"
        onConfirm={handleDeleteAlias}
        variant="destructive"
        loading={loading}
      />

      <BaseDialog
        open={deletingDriver}
        onOpenChange={handleCloseDeleteDialog}
        title="Ta bort förare"
        size="sm"
        footer={
          <div className="flex gap-3 justify-end">
            <Button
              variant="outline"
              onClick={() => handleCloseDeleteDialog(false)}
              disabled={deletingDriverLoading}
            >
              Avbryt
            </Button>
            <Button
              variant="destructive"
              onClick={handleDeleteDriver}
              disabled={deletingDriverLoading || (!!hasDataInOpenPeriods && !deleteDataConfirmed)}
            >
              {deletingDriverLoading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Ta bort förare
            </Button>
          </div>
        }
      >
        <div className="space-y-4">
          <div className="flex items-start gap-4">
            <AlertCircle className="h-8 w-8 text-red-500 flex-shrink-0" />
            <div className="flex-1">
              <p className="text-sm text-muted-foreground">
                Är du säker på att du vill ta bort föraren <strong>{driver?.primaryName}</strong>?
                Denna åtgärd kan inte ångras.
              </p>
            </div>
          </div>

          {hasDataInOpenPeriods && (
            <>
              <Alert>
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>
                  <div className="space-y-2">
                    <p>Föraren har data i öppna perioder:</p>
                    <ul className="list-disc list-inside text-sm">
                      {driverDataCounts!.earningsCount > 0 && (
                        <li>{driverDataCounts!.earningsCount} intäkt{driverDataCounts!.earningsCount !== 1 ? 'er' : ''}</li>
                      )}
                      {driverDataCounts!.compensationsCount > 0 && (
                        <li>{driverDataCounts!.compensationsCount} kompensation{driverDataCounts!.compensationsCount !== 1 ? 'er' : ''}</li>
                      )}
                    </ul>
                    <p className="text-xs text-muted-foreground mt-2">
                      Data i låsta perioder bevaras i ögonblicksbilder.
                    </p>
                  </div>
                </AlertDescription>
              </Alert>

              <div className="flex items-center space-x-2">
                <Checkbox
                  id="deleteData"
                  checked={deleteDataConfirmed}
                  onCheckedChange={(checked) => setDeleteDataConfirmed(checked === true)}
                />
                <label
                  htmlFor="deleteData"
                  className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                >
                  Radera även intäkter och kompensationer i öppna perioder
                </label>
              </div>
            </>
          )}
        </div>
      </BaseDialog>
    </PageContainer>
  )
}